import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Select } from "antd";
import { checkPermission, hasAdminAccess, permissionType, canReadSalarySlips, isOwnSalarySlipPageMode, canViewSalarySlips, canAccessSalarySlipDownload } from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import { sendSalarySlipEmail } from "../../../app/utils/common/salarySlipEmail";
import {
  buildSalarySlipMailPlainText,
  buildSalarySlipMailSubject,
  getSalarySlipMailAttachmentName,
} from "../../../app/utils/common/salarySlipMail";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import { APIS } from "../../../app/utils/constants/apiConstant";
import {
  formatCurrency,
  getDefaultFinancePeriod,
  getDefaultEmployeeSalarySlipPeriod,
  getEmployeeSalarySlipMonthOptions,
  getEmployeeSalarySlipStartLabel,
  getEmployeeSalarySlipYearOptions,
  getFinanceMonthOptions,
  getFinanceYearOptions,
  getMonthLabel,
} from "../../../app/utils/finance/salaryCalculations";
import {
  buildSalarySlipPdfBlob,
  createSalarySlipPreviewUrl,
  downloadSalarySlipPdf,
} from "../../../app/utils/finance/salarySlipPdf";
import { readPayslipCompanyByIdSync } from "../../../app/utils/finance/payslipCompanyDetails";
import {
  DEFAULT_SALARY_COMPANY_NAME,
  financeCompaniesForTabs,
  findTheCodingStudioCompany,
  getTheCodingStudioTabId,
  isTheCodingStudioTabActive,
  slipBelongsToTheCodingStudioTab,
} from "../../../app/utils/company/financeDefaultSalaryCompany";
import { SALARY_SLIP_CC_RECIPIENT_OPTIONS } from "../../../app/utils/constants/dailyHoursNotifyRecipients";
import {
  FINANCE_SALARY_COMPANIES_UPDATED_EVENT,
  getFinanceSalaryCompanyLabelSync,
  readFinanceSalaryCompanies,
} from "../../../app/utils/company/financeSalaryCompaniesStorage";
import SalarySlipCompanyDetailsModal from "../../molecules/SalarySlipCompanyDetailsModal/SalarySlipCompanyDetailsModal";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import "./SalarySlipPage.scss";

const defaultPeriod = getDefaultFinancePeriod();

const isValidEmailList = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return true;
  return raw
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean)
    .every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
};

const parseEmailList = (value) =>
  String(value || "")
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean);

const buildCcEmailList = ({ selectedEmails = [], manualCc = "" } = {}) =>
  [...new Set([...selectedEmails, ...parseEmailList(manualCc)])].join(", ");

const SalarySlipPage = () => {
  const { user } = useSelector((state) => state.auth.auth);
  const location = useLocation();
  const [filterYear, setFilterYear] = useState(defaultPeriod.year);
  const [filterMonth, setFilterMonth] = useState(defaultPeriod.month);
  const [search, setSearch] = useState("");
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companyModalContext, setCompanyModalContext] = useState({ key: "", label: "" });
  const [activeSalaryCompanyId, setActiveSalaryCompanyId] = useState("");
  const [preview, setPreview] = useState(null);
  const [companyProfilesVersion, setCompanyProfilesVersion] = useState(0);
  const [companyProfiles, setCompanyProfiles] = useState([]);
  const [showSendMailModal, setShowSendMailModal] = useState(false);
  const [slipToSendMail, setSlipToSendMail] = useState(null);
  const [slipMailDraft, setSlipMailDraft] = useState({ subject: "", body: "", cc: "", bcc: "" });
  const [ccSelectedEmails, setCcSelectedEmails] = useState([]);
  const [sendingSlipMail, setSendingSlipMail] = useState(false);
  const [mailAttachmentPreview, setMailAttachmentPreview] = useState(null);
  const slipMailDraftRef = useRef({ subject: "", body: "", cc: "", bcc: "" });

  const isOwnSalarySlipView = isOwnSalarySlipPageMode(user, location.pathname);
  const isAdminView = canReadSalarySlips(user) && !isOwnSalarySlipView;
  const canRead = canViewSalarySlips(user);
  const canDownload = canAccessSalarySlipDownload(user);
  const canSendMail =
    isAdminView && (hasAdminAccess(user) || checkPermission(permissionType.SALARY_SLIP, "send mail"));

  const loadCompanyProfiles = useCallback(async () => {
    if (!isAdminView) {
      setCompanyProfiles([]);
      return;
    }
    try {
      const companies = await readFinanceSalaryCompanies();
      setCompanyProfiles(companies);
    } catch (error) {
      showErrorToast(
        error?.response?.data?.error?.message || "Failed to load finance companies."
      );
      setCompanyProfiles([]);
    }
  }, [isAdminView]);

  useEffect(() => {
    loadCompanyProfiles();
  }, [loadCompanyProfiles, companyProfilesVersion]);

  useEffect(() => {
    if (!isAdminView) return;
    const onCompaniesUpdated = () => setCompanyProfilesVersion((v) => v + 1);
    window.addEventListener(FINANCE_SALARY_COMPANIES_UPDATED_EVENT, onCompaniesUpdated);
    return () => window.removeEventListener(FINANCE_SALARY_COMPANIES_UPDATED_EVENT, onCompaniesUpdated);
  }, [isAdminView]);

  const adminName = useMemo(() => {
    const name = [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim();
    return name || user?.email || "Admin";
  }, [user]);

  useEffect(() => {
    slipMailDraftRef.current = slipMailDraft;
  }, [slipMailDraft]);

  const yearOptions = useMemo(
    () => (isOwnSalarySlipView ? getEmployeeSalarySlipYearOptions() : getFinanceYearOptions()),
    [isOwnSalarySlipView]
  );
  const monthOptions = useMemo(
    () =>
      isOwnSalarySlipView
        ? getEmployeeSalarySlipMonthOptions(filterYear)
        : getFinanceMonthOptions(filterYear),
    [isOwnSalarySlipView, filterYear]
  );
  const employeeSlipStartLabel = getEmployeeSalarySlipStartLabel();

  useEffect(() => {
    if (!isOwnSalarySlipView) return;
    const period = getDefaultEmployeeSalarySlipPeriod();
    setFilterYear(period.year);
    setFilterMonth(period.month);
  }, [isOwnSalarySlipView]);

  const ccRecipientOptions = useMemo(() => {
    const employeeEmail = String(slipToSendMail?.employeeEmail || "")
      .trim()
      .toLowerCase();
    return SALARY_SLIP_CC_RECIPIENT_OPTIONS.filter((option) => {
      const email = String(option.value || "").trim().toLowerCase();
      return email && email !== employeeEmail;
    });
  }, [slipToSendMail?.employeeEmail]);

  const resolveSelectedCcEmails = useCallback(
    (selectedEmails = ccSelectedEmails) => selectedEmails.filter(Boolean),
    [ccSelectedEmails]
  );

  const resolveMailCcValue = useCallback(
    (manualCc = slipMailDraft.cc, selectedEmails = ccSelectedEmails) =>
      buildCcEmailList({
        selectedEmails: resolveSelectedCcEmails(selectedEmails),
        manualCc,
      }),
    [ccSelectedEmails, resolveSelectedCcEmails, slipMailDraft.cc]
  );

  const handleYearChange = (year) => {
    setFilterYear(year);
    const allowedMonths = isOwnSalarySlipView
      ? getEmployeeSalarySlipMonthOptions(year)
      : getFinanceMonthOptions(year);
    if (!allowedMonths.some((option) => option.value === filterMonth)) {
      const last = allowedMonths[allowedMonths.length - 1];
      if (last) setFilterMonth(last.value);
    }
  };

  const fetchSyncedSlip = useCallback(async (slip) => {
    if (!slip?.id) return slip;
    const response = await protectedApi({
      url: `${APIS.SALARY_SLIP.replace(":slipId", String(slip.id))}?sync=1`,
      method: "GET",
    });
    return response?.data?.data?.slip || slip;
  }, []);

  const loadSlips = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        year: String(filterYear),
        month: String(filterMonth),
        sync: "1",
      });
      if (isOwnSalarySlipView) params.set("mine", "1");
      if (!isOwnSalarySlipView && search.trim()) params.set("search", search.trim());
      const response = await protectedApi({
        url: `${APIS.SALARY_SLIPS}?${params.toString()}`,
        method: "GET",
      });
      let rows = response?.data?.data?.slips || [];
      if (isOwnSalarySlipView) {
        const ownUserId = Number(user?.id ?? user?.userId);
        if (ownUserId) {
          rows = rows.filter((slip) => Number(slip.userId) === ownUserId);
        }
      }
      setSlips(rows);
    } catch (error) {
      showErrorToast(error?.response?.data?.error?.message || "Failed to load salary slips.");
      setSlips([]);
    } finally {
      setLoading(false);
    }
  }, [canRead, filterYear, filterMonth, search, isOwnSalarySlipView, user?.id, user?.userId]);

  useEffect(() => {
    loadSlips();
  }, [loadSlips]);

  const otherCompanyProfiles = useMemo(
    () => financeCompaniesForTabs(companyProfiles),
    [companyProfiles]
  );
  const theCodingStudioTabId = useMemo(
    () => getTheCodingStudioTabId(companyProfiles),
    [companyProfiles]
  );

  useEffect(() => {
    const tabIds = [
      getTheCodingStudioTabId(companyProfiles),
      ...financeCompaniesForTabs(companyProfiles).map((company) => String(company.id)),
    ];
    setActiveSalaryCompanyId((current) => {
      if (current && tabIds.includes(current)) return current;
      if (current && isTheCodingStudioTabActive(current, companyProfiles)) {
        return getTheCodingStudioTabId(companyProfiles);
      }
      return tabIds[0] || getTheCodingStudioTabId([]);
    });
  }, [companyProfiles]);

  useEffect(
    () => () => {
      preview?.revoke?.();
      mailAttachmentPreview?.revoke?.();
    },
    [preview, mailAttachmentPreview]
  );

  const filteredSlips = useMemo(() => {
    if (isOwnSalarySlipView) return slips;
    if (!activeSalaryCompanyId) return slips;
    if (isTheCodingStudioTabActive(activeSalaryCompanyId, companyProfiles)) {
      return slips.filter((slip) => slipBelongsToTheCodingStudioTab(slip, companyProfiles));
    }
    return slips.filter(
      (slip) => String(slip.salaryCompanyId || "").trim() === String(activeSalaryCompanyId)
    );
  }, [slips, isOwnSalarySlipView, activeSalaryCompanyId, companyProfiles]);

  const payslipDetailsCompanyKey = useMemo(() => {
    if (isTheCodingStudioTabActive(activeSalaryCompanyId, companyProfiles)) {
      return findTheCodingStudioCompany(companyProfiles)?.id ?? activeSalaryCompanyId;
    }
    return activeSalaryCompanyId;
  }, [activeSalaryCompanyId, companyProfiles]);

  const payslipDetailsCompanyLabel = isTheCodingStudioTabActive(
    activeSalaryCompanyId,
    companyProfiles
  )
    ? DEFAULT_SALARY_COMPANY_NAME
    : getFinanceSalaryCompanyLabelSync(activeSalaryCompanyId, companyProfiles);

  const resolveSlipCompanyDetails = (slip) => {
    const embedded = slip?.payslipCompany;
    if (embedded?.companyName || embedded?.ownerName || embedded?.signatureUrl) {
      return {
        id: embedded.id != null ? String(embedded.id) : "",
        companyName: embedded.companyName || "",
        ownerName: embedded.ownerName || "",
        contactNumber: embedded.contactNumber || "",
        emailId: embedded.emailId || "",
        companyAddress: embedded.companyAddress || "",
        signatureFileName: embedded.signatureFileName || "",
        signatureDataUrl: embedded.signatureUrl || "",
      };
    }
    const companyId = String(slip?.salaryCompanyId || "").trim();
    return readPayslipCompanyByIdSync(companyId, companyProfiles);
  };

  const closePreview = () => {
    setPreview((current) => {
      current?.revoke?.();
      return null;
    });
  };

  const getSlipEmployeeEmail = (slip) => String(slip?.employeeEmail || "").trim();

  const isSlipEmailSent = (slip) => Boolean(slip?.emailSentAt);

  const openSendMailDialog = (slip) => {
    if (isSlipEmailSent(slip)) return;
    const company = resolveSlipCompanyDetails(slip);
    setSlipToSendMail(slip);
    setCcSelectedEmails([]);
    setSlipMailDraft({
      subject: buildSalarySlipMailSubject({ slip }),
      body: buildSalarySlipMailPlainText({
        slip,
        senderName: company?.ownerName || adminName,
        senderTitle: "HR Team",
        companyName: company?.companyName || "",
      }),
      cc: "",
      bcc: "",
    });
    setShowSendMailModal(true);
  };

  const resetSendMailDialog = () => {
    setShowSendMailModal(false);
    setSlipToSendMail(null);
    setCcSelectedEmails([]);
    setSlipMailDraft({ subject: "", body: "", cc: "", bcc: "" });
    setMailAttachmentPreview((current) => {
      current?.revoke?.();
      return null;
    });
  };

  const closeSendMailDialog = () => {
    if (sendingSlipMail) return;
    resetSendMailDialog();
  };

  const handleViewMailAttachment = async () => {
    if (!slipToSendMail || mailAttachmentPreview?.url) {
      if (mailAttachmentPreview?.url) {
        setPreview({
          ...mailAttachmentPreview,
          title: slipToSendMail?.employeeName || "Salary slip",
          slipId: slipToSendMail?.id,
        });
      }
      return;
    }

    try {
      mailAttachmentPreview?.revoke?.();
      const nextPreview = await createSalarySlipPreviewUrl(
        slipToSendMail,
        resolveSlipCompanyDetails(slipToSendMail)
      );
      setMailAttachmentPreview(nextPreview);
      setPreview({
        ...nextPreview,
        title: slipToSendMail.employeeName || "Salary slip",
        slipId: slipToSendMail.id,
      });
    } catch (error) {
      showErrorToast(error?.message || "Failed to open salary slip preview.");
    }
  };

  const handleSendSlipMail = async () => {
    if (!slipToSendMail || sendingSlipMail) return;
    if (isSlipEmailSent(slipToSendMail)) {
      showErrorToast("Salary slip email has already been sent.");
      return;
    }
    if (!canSendMail) {
      showErrorToast("You do not have permission to send salary slip emails.");
      return;
    }

    const employeeEmail = getSlipEmployeeEmail(slipToSendMail);
    if (!employeeEmail) {
      showErrorToast("Employee email is required to send salary slip. Add it in Employee Management.");
      return;
    }

    const mailDraft = slipMailDraftRef.current;
    if (!mailDraft.subject.trim() || !mailDraft.body.trim()) {
      showErrorToast("Email subject and message are required.");
      return;
    }
    if (!isValidEmailList(mailDraft.cc)) {
      showErrorToast("Enter valid additional CC emails separated by commas.");
      return;
    }
    if (!isValidEmailList(mailDraft.bcc)) {
      showErrorToast("Enter valid BCC emails separated by commas.");
      return;
    }

    const cc = resolveMailCcValue(mailDraft.cc);
    if (cc && !isValidEmailList(cc)) {
      showErrorToast("Enter valid CC emails.");
      return;
    }

    const company = resolveSlipCompanyDetails(slipToSendMail);
    setSendingSlipMail(true);
    try {
      const freshSlip = await fetchSyncedSlip(slipToSendMail);
      const { blob, fileName } = await buildSalarySlipPdfBlob(freshSlip, company);

      await sendSalarySlipEmail({
        pdfBlob: blob,
        pdfFileName: fileName,
        to: employeeEmail,
        subject: mailDraft.subject,
        body: mailDraft.body,
        emailSubject: mailDraft.subject,
        emailBody: mailDraft.body,
        cc,
        bcc: mailDraft.bcc,
        slipId: slipToSendMail.id,
        slip: {
          id: slipToSendMail.id,
          slipNumber: slipToSendMail.slipNumber,
          employeeName: slipToSendMail.employeeName,
          sheetYear: slipToSendMail.sheetYear,
          sheetMonth: slipToSendMail.sheetMonth,
          paidAt: slipToSendMail.paidAt,
        },
        senderName: company?.ownerName || adminName,
        senderTitle: "HR Team",
        companyName: company?.companyName || "",
        replyTo: company?.emailId || undefined,
      });
      const sentAt = new Date().toISOString();
      setSlips((current) =>
        current.map((row) =>
          row.id === slipToSendMail.id
            ? { ...row, emailSentAt: sentAt, emailSentByUserId: user?.userId || user?.id || null }
            : row
        )
      );
      showSuccessToast("Salary slip email sent with PDF attachment.");
      resetSendMailDialog();
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unable to send salary slip email.";
      if (status === 413) {
        showErrorToast("Attachment size is too large to send.");
      } else if (/already been sent/i.test(String(message))) {
        setSlips((current) =>
          current.map((row) =>
            row.id === slipToSendMail.id ? { ...row, emailSentAt: row.emailSentAt || new Date().toISOString() } : row
          )
        );
        showErrorToast(typeof message === "string" ? message : "Salary slip email has already been sent.");
        resetSendMailDialog();
      } else {
        showErrorToast(typeof message === "string" ? message : "Unable to send salary slip email.");
      }
    } finally {
      setSendingSlipMail(false);
    }
  };

  const getSlipMailPreview = (slip) => {
    if (!slip) return null;
    const cc = resolveMailCcValue();
    return {
      to: getSlipEmployeeEmail(slip) || "—",
      cc: cc || "—",
      bcc: slipMailDraft.bcc || "—",
      subject: slipMailDraft.subject || buildSalarySlipMailSubject({ slip }),
      attachments: [getSalarySlipMailAttachmentName(slip)],
    };
  };

  const handleView = async (slip) => {
    if (!canRead) return;
    setViewingId(slip.id);
    try {
      preview?.revoke?.();
      const freshSlip = await fetchSyncedSlip(slip);
      const nextPreview = await createSalarySlipPreviewUrl(freshSlip, resolveSlipCompanyDetails(freshSlip));
      setPreview({
        ...nextPreview,
        title: freshSlip.employeeName || "Salary slip",
        slipId: freshSlip.id,
      });
    } catch (error) {
      showErrorToast(error?.message || "Failed to open salary slip preview.");
    } finally {
      setViewingId(null);
    }
  };

  const handlePreviewDownload = () => {
    if (!preview?.url) return;
    const link = document.createElement("a");
    link.href = preview.url;
    link.download = preview.fileName || "salary-slip.pdf";
    link.click();
  };

  const handleDownload = async (slip) => {
    if (!canDownload) return;
    setDownloadingId(slip.id);
    try {
      const freshSlip = await fetchSyncedSlip(slip);
      await downloadSalarySlipPdf(freshSlip, resolveSlipCompanyDetails(freshSlip));
    } catch (error) {
      showErrorToast(error?.message || "Failed to download salary slip PDF.");
    } finally {
      setDownloadingId(null);
    }
  };

  const showActions = canRead || canDownload || canSendMail;
  const slipBusy = (slipId) =>
    viewingId === slipId || downloadingId === slipId || (sendingSlipMail && slipToSendMail?.id === slipId);

  if (!canRead) {
    return (
      <div className="salary-slip-page p-6">
        <p className="text-sm text-[#6f7d97]">You do not have permission to view salary slips.</p>
      </div>
    );
  }

  return (
    <div className="salary-slip-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="page-title">{isOwnSalarySlipView ? "My Salary Slips" : "Salary Slips"}</h1>
          <p className="text-sm text-[#6f7d97] mt-1">
            {isOwnSalarySlipView
              ? `View and download your payslips from ${employeeSlipStartLabel} onwards, once salary has been marked as paid.`
              : "Payslips are created automatically when an employee's salary is marked as paid in Finance Management."}
          </p>
        </div>
        {isAdminView ? (
          <button
            type="button"
            className="button border border-[#e8ecf3] text-[#27314a] px-5 py-2 rounded-lg text-sm whitespace-nowrap"
            onClick={() => {
              setCompanyModalContext({
                key: payslipDetailsCompanyKey,
                label: payslipDetailsCompanyLabel,
              });
              setCompanyModalOpen(true);
            }}
          >
            Payslip details
            {payslipDetailsCompanyLabel ? ` (${payslipDetailsCompanyLabel})` : ""}
          </button>
        ) : null}
      </div>

      <SalarySlipCompanyDetailsModal
        open={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        companyKey={companyModalContext.key}
        companyLabel={companyModalContext.label}
        onSaved={() => setCompanyProfilesVersion((v) => v + 1)}
      />

      <Modal
        className="salary-slip-preview-modal"
        title={preview?.title ? `Salary slip — ${preview.title}` : "Salary slip preview"}
        open={Boolean(preview?.url)}
        onCancel={closePreview}
        width="min(920px, 96vw)"
        centered
        destroyOnClose
        footer={[
          canDownload ? (
            <button
              key="download"
              type="button"
              className="button bg-btn-primary text-white px-5 py-2 rounded-lg"
              onClick={handlePreviewDownload}
            >
              Download PDF
            </button>
          ) : null,
          <button
            key="close"
            type="button"
            className="button border border-[#e8ecf3] text-[#27314a] px-5 py-2 rounded-lg"
            onClick={closePreview}
          >
            Close
          </button>,
        ]}
      >
        {preview?.url ? (
          <iframe
            title="Salary slip preview"
            className="salary-slip-preview-frame"
            src={preview.url}
          />
        ) : null}
      </Modal>

      <Modal
        title="Send mail"
        open={showSendMailModal}
        onOk={handleSendSlipMail}
        onCancel={closeSendMailDialog}
        okText={sendingSlipMail ? "Sending…" : "Send email"}
        cancelText="Cancel"
        confirmLoading={sendingSlipMail}
        okButtonProps={{
          disabled:
            sendingSlipMail ||
            !getSlipEmployeeEmail(slipToSendMail) ||
            !slipMailDraft.subject.trim() ||
            !slipMailDraft.body.trim(),
        }}
        cancelButtonProps={{ disabled: sendingSlipMail }}
        width={760}
        className="invoice-mail-preview-modal"
      >
        {slipToSendMail ? (
          (() => {
            const mailPreview = getSlipMailPreview(slipToSendMail);
            return (
              <div className="invoice-mail-preview">
                {!getSlipEmployeeEmail(slipToSendMail) ? (
                  <p className="invoice-mail-preview__warning">
                    Add employee email in Employee Management before sending.
                  </p>
                ) : null}
                <div className="invoice-mail-preview__meta">
                  <div>
                    <span className="invoice-mail-preview__label">To</span>
                    <span>{mailPreview.to}</span>
                  </div>
                  <div>
                    <span className="invoice-mail-preview__label">CC</span>
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      className="invoice-mail-preview__cc-select"
                      placeholder="Select CC recipients"
                      value={ccSelectedEmails}
                      onChange={setCcSelectedEmails}
                      options={ccRecipientOptions}
                      disabled={sendingSlipMail}
                      notFoundContent="No CC recipients available"
                    />
                    {/* <input
                      className="invoice-mail-preview__input invoice-mail-preview__input--secondary"
                      value={slipMailDraft.cc}
                      onChange={(event) =>
                        setSlipMailDraft((current) => ({ ...current, cc: event.target.value }))
                      }
                      placeholder="Additional CC emails, comma separated (optional)"
                      disabled={sendingSlipMail}
                    /> */}
                  </div>
                  <div>
                    <span className="invoice-mail-preview__label">BCC</span>
                    <input
                      className="invoice-mail-preview__input"
                      value={slipMailDraft.bcc}
                      onChange={(event) =>
                        setSlipMailDraft((current) => ({ ...current, bcc: event.target.value }))
                      }
                      placeholder="Optional BCC emails, comma separated"
                    />
                  </div>
                  <div>
                    <span className="invoice-mail-preview__label">Subject</span>
                    <input
                      className="invoice-mail-preview__input"
                      value={slipMailDraft.subject}
                      onChange={(event) =>
                        setSlipMailDraft((current) => ({ ...current, subject: event.target.value }))
                      }
                      placeholder="Email subject"
                    />
                  </div>
                </div>
                <div className="invoice-mail-preview__body">
                  <textarea
                    className="invoice-mail-preview__textarea"
                    value={slipMailDraft.body}
                    onChange={(event) =>
                      setSlipMailDraft((current) => ({ ...current, body: event.target.value }))
                    }
                    rows={12}
                    placeholder="Email message"
                  />
                </div>
                <div className="invoice-mail-preview__attachments">
                  <span className="invoice-mail-preview__label">Attachments</span>
                  <ul>
                    {mailPreview.attachments.map((name) => (
                      <li key={name} className="salary-slip-mail-attachment">
                        <span>{name}</span>
                        <button
                          type="button"
                          className="salary-slip-action-btn salary-slip-action-btn--view"
                          onClick={handleViewMailAttachment}
                        >
                          View
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })()
        ) : null}
      </Modal>

      <section className="card-border card-body">

        {!isOwnSalarySlipView ? (
          <nav className="salary-slip-company-tabs" aria-label="Salary company">
            <button
              type="button"
              className={`salary-slip-company-tab ${isTheCodingStudioTabActive(activeSalaryCompanyId, companyProfiles) ? "active" : ""
                }`}
              onClick={() => setActiveSalaryCompanyId(theCodingStudioTabId)}
            >
              {DEFAULT_SALARY_COMPANY_NAME}
            </button>
            {otherCompanyProfiles.map((company) => {
              const companyId = String(company.id);
              return (
                <button
                  key={companyId}
                  type="button"
                  className={`salary-slip-company-tab ${activeSalaryCompanyId === companyId ? "active" : ""}`}
                  onClick={() => setActiveSalaryCompanyId(companyId)}
                >
                  {company.companyName?.trim() || `Company ${companyId}`}
                </button>
              );
            })}
          </nav>
        ) : null}
        <div className="salary-slip-filters">
          <div>
            <label className="block text-xs text-[#6f7d97] mb-1">Year</label>
            <Select
              value={filterYear}
              onChange={handleYearChange}
              options={yearOptions}
              className="min-w-[120px]"
            />
          </div>
          <div>
            <label className="block text-xs text-[#6f7d97] mb-1">Month</label>
            <Select
              value={filterMonth}
              onChange={setFilterMonth}
              options={monthOptions}
              className="min-w-[140px]"
            />
          </div>
          {!isOwnSalarySlipView ? (
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-[#6f7d97] mb-1">Search employee</label>
              <input
                type="search"
                className="w-full border border-[#e8ecf3] rounded-lg px-3 py-2 text-sm"
                placeholder="Name, slip number, designation…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadSlips();
                }}
              />
            </div>
          ) : null}
          <button
            type="button"
            className="button bg-btn-primary text-white px-5 py-2 rounded-lg"
            onClick={loadSlips}
            disabled={loading}
          >
            {loading ? "Loading…" : "Apply"}
          </button>
        </div>

        <p className="text-sm text-[#6f7d97] mb-4">
          {loading
            ? `Loading slips for ${getMonthLabel(filterMonth)} ${filterYear}…`
            : filteredSlips.length
              ? `${filteredSlips.length} salary slip(s) for ${getMonthLabel(filterMonth)} ${filterYear}${!isOwnSalarySlipView && isTheCodingStudioTabActive(activeSalaryCompanyId, companyProfiles)
                ? ` · ${DEFAULT_SALARY_COMPANY_NAME}`
                : !isOwnSalarySlipView && activeSalaryCompanyId
                  ? ` · ${getFinanceSalaryCompanyLabelSync(activeSalaryCompanyId, companyProfiles)}`
                  : ""
              }.`
              : slips.length && !isOwnSalarySlipView
                ? `No salary slips for the selected company. Try another company tab.`
                : isOwnSalarySlipView
                  ? `No salary slip for ${getMonthLabel(filterMonth)} ${filterYear} yet. Your slip will appear here once salary is paid for that month.`
                  : `No salary slips for ${getMonthLabel(filterMonth)} ${filterYear}. Mark salaries as paid in Finance Management to generate slips.`}
        </p>

        <div className="salary-slip-table-wrap">
          <table className="salary-slip-table">
            <thead>
              <tr>
                <th>Slip No.</th>
                {!isOwnSalarySlipView ? <th>Employee</th> : null}
                <th>Designation</th>
                <th>Gross</th>
                <th>Deductions</th>
                <th>Net salary</th>
                <th>Paid on</th>
                {showActions ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody >
              {filteredSlips.map((slip) => (
                <tr key={slip.id} className="text-[#27314a]">
                  <td>{slip.slipNumber}</td>
                  {!isOwnSalarySlipView ? <td>{slip.employeeName}</td> : null}
                  <td>{slip.designation || "—"}</td>
                  <td>{formatCurrency(slip.grossEarnings)}</td>
                  <td>{formatCurrency(slip.totalDeduction)}</td>
                  <td className="net-cell">{formatCurrency(slip.netSalary)}</td>
                  <td>
                    {slip.paidAt
                      ? new Date(slip.paidAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                      : "—"}
                  </td>
                  {showActions ? (
                    <td>
                      <div className="salary-slip-page-actions">
                        {canRead ? (
                          <button
                            type="button"
                            className="salary-slip-action-btn salary-slip-action-btn--view"
                            disabled={slipBusy(slip.id)}
                            onClick={() => handleView(slip)}
                          >
                            {viewingId === slip.id ? "Loading…" : "View"}
                          </button>
                        ) : null}
                        {canDownload ? (
                          <button
                            type="button"
                            className="salary-slip-action-btn salary-slip-action-btn--download"
                            disabled={slipBusy(slip.id)}
                            onClick={() => handleDownload(slip)}
                          >
                            {downloadingId === slip.id ? "Preparing…" : "Download"}
                          </button>
                        ) : null}
                        {canSendMail ? (
                          isSlipEmailSent(slip) ? (
                            <span
                              className="salary-slip-action-btn salary-slip-action-btn--sent"
                              title={
                                slip.emailSentAt
                                  ? `Sent on ${new Date(slip.emailSentAt).toLocaleString("en-IN")}`
                                  : "Email sent"
                              }
                            >
                              Sent
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="salary-slip-action-btn salary-slip-action-btn--mail"
                              disabled={slipBusy(slip.id)}
                              onClick={() => openSendMailDialog(slip)}
                            >
                              Send Mail
                            </button>
                          )
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          {!filteredSlips.length && !loading ? (
            <p className="p-6 text-center text-sm text-[#6f7d97]">No salary slips to display.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default SalarySlipPage;
